import React from 'react';

import { Input, InputNumber, Rate, Select } from 'antd';

import styled from 'styled-components';

import { getDisplayValues, groupConversionValues } from '@libs/NwFeatures';

import NwFieldAddOnBefore from '@components/Gui/NwForm/NwFieldAddOnBefore';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import { StyledRateContainer } from '@components/Gui/NwForm/Rating';
import NwIcon, { NwBoolIcon } from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Option = Select.Option;
const { TextArea } = Input;

const RatingContainer = styled(StyledRateContainer)`
    display: flex;
    align-items: center;

    .close {
        font-size: 1.3rem;
        margin-left: 8px;
    }
`;

const FeatureValueField = ({
    item,
    value,
    setFieldValue,
    featuresIndex,
    onUpdateFeature,
    disabled,
    setUpdatedFieldIDs,
    setCurrentUpdatingFieldID,
    gender,
    addOnBefore,
}) => {
    let component;

    const handleUpdateFormItem = async (fieldID, value) => {
        await setFieldValue(fieldID, value);
        await setUpdatedFieldIDs(fieldID);
        await setCurrentUpdatingFieldID(item.ID);
        onUpdateFeature();
    };

    switch (item.Feature.TypeName) {
        case 'Conversion':
            // la conversion table può non esserci più fra quelle globali: il campo resta, senza opzioni,
            // invece di far cadere tutto il drawer
            const conversionValues = (item.Feature.conversion && item.Feature.conversion.ConversionValues) || [];
            const options = groupConversionValues(conversionValues);

            component = (
                <Select
                    autoComplete="off"
                    showSearch
                    name={item.Feature.Name}
                    id={item.ID}
                    style={{ width: '100%' }}
                    value={value}
                    disabled={disabled}
                    onChange={(val) => handleUpdateFormItem(item.ID, val)}
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                    <Option key={`0`} value="">
                        (x) no value
                    </Option>
                    {options.map((option, index) => {
                        return (
                            <Option key={`${option.Key}${index}`} value={String(option.Key)}>
                                {/* senza fallback un'opzione priva di etichetta farebbe mostrare ad antd il
                                    suo value, cioè il Key interno della conversion */}
                                {getDisplayValues(option.DisplayValue, item.Feature.ConversionID, featuresIndex, {
                                    separator: ' • ',
                                    gender,
                                    fallbackToAllMetrics: true,
                                })}
                            </Option>
                        );
                    })}
                </Select>
            );
            break;
        case 'Choice':
            component = (
                <Select
                    autoComplete="off"
                    showSearch
                    name={item.Feature.Name}
                    id={item.ID}
                    style={{ width: '100%' }}
                    value={value}
                    disabled={disabled}
                    onChange={(val) => handleUpdateFormItem(item.ID, val)}
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                    <Option key={`0`} value="">
                        (x) no value
                    </Option>
                    {(item.Feature.Choices || []).map((choice, i) => {
                        return (
                            <Option key={`${item.Feature.Name}${choice.ID}${i}`} value={String(choice.ID)}>
                                {choice.Value}
                            </Option>
                        );
                    })}
                </Select>
            );
            break;
        case 'Multichoice':
            component = (
                <Select
                    autoComplete="off"
                    showSearch
                    mode="multiple"
                    name={item.Feature.Name}
                    value={value ? value.split(',') : []}
                    id={item.ID}
                    disabled={disabled}
                    style={{ width: '100%' }}
                    onChange={(val) => {
                        handleUpdateFormItem(item.ID, val.join(','));
                    }}
                    filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                    {(item.Feature.Choices || []).map((el) => (
                        <Option key={el.ID} value={String(el.ID)}>
                            {el.Value}
                        </Option>
                    ))}
                </Select>
            );
            break;
        case 'BigText':
            component = (
                <TextArea
                    rows={4}
                    autoComplete="off"
                    name={item.Feature.Name}
                    id={item.ID}
                    autoSize={true}
                    value={value}
                    disabled={disabled}
                    onBlur={() => {
                        handleUpdateFormItem(item.ID, value);
                    }}
                    onChange={(e) => {
                        setFieldValue(item.ID, e.target.value);
                    }}
                    type="text"
                />
            );
            break;
        case 'Text':
            component = (
                <Input
                    autoComplete="off"
                    name={item.Feature.Name}
                    id={item.ID}
                    value={value}
                    disabled={disabled}
                    onBlur={() => {
                        handleUpdateFormItem(item.ID, value);
                    }}
                    onChange={(e) => {
                        setFieldValue(item.ID, e.target.value);
                    }}
                    type="text"
                />
            );
            break;
        case 'Boolean':
            component = (
                <RadioButtonGroup
                    name={item.Feature.Name}
                    value={value}
                    disabled={disabled}
                    id={item.ID}
                    onChange={(value) => {
                        handleUpdateFormItem(item.ID, value);
                    }}
                    options={[
                        { label: <NwBoolIcon />, value: 'true' },
                        { label: <NwBoolIcon value={false} />, value: 'false' },
                        { label: <NwIcon icon={byPrefixAndName.fasl['times']} />, value: '' },
                    ]}
                />
            );
            break;
        case 'Rating':
            const ratingColor = {
                mainColor: StyleVariables.PrimaryColor,
                lightColor: StyleVariables.PrimaryColorLight,
            };
            component = (
                <RatingContainer ratingColor={ratingColor}>
                    <Rate
                        name={item.Feature.Name}
                        disabled={disabled}
                        style={{ fontSize: '20px' }}
                        // dall'API il valore è una stringa: l'azzeramento al riclick confronta con === e senza
                        // conversione non scatta mai (Number(null) e Number('') danno 0, cioè nessuna stella)
                        value={Number(value) || 0}
                        id={item.ID}
                        onChange={(value) => {
                            // la scala parte da 1: zero stelle è assenza di valore, non il valore zero
                            handleUpdateFormItem(item.ID, value || null);
                        }}
                    />
                    <NwIcon
                        className="close"
                        icon={byPrefixAndName.fasl['times']}
                        pointer
                        onClick={() => handleUpdateFormItem(item.ID, null)}
                    />
                </RatingContainer>
            );
            break;
        case 'Numeric':
            component = (
                <InputNumber
                    value={value}
                    step="0.1"
                    style={{ width: '100%' }}
                    onBlur={() => {
                        handleUpdateFormItem(item.ID, value);
                    }}
                    onChange={(e) => {
                        setFieldValue(item.ID, e);
                    }}
                />
            );
            break;
        default:
            component = null;
            break;
    }

    return (
        <NwFieldAddOnBefore addOnBefore={addOnBefore} plain={item.Feature.TypeName === 'Boolean'}>
            {component}
        </NwFieldAddOnBefore>
    );
};

export default FeatureValueField;
